/**
 * Transform functions for Cancer benefits
 *
 * These functions handle the bi-directional transformation between:
 * - Frontend grid format (flat fields with separate type and amount/duration)
 * - Backend API format (nested objects with type and amount/duration)
 */

import {
  BaseCancerBenefitInitialDiagnosisWaitingPeriodType,
  PreExistingConditionTypeEnum,
  RfpCancerHospiceBenefitType,
  RfpInclusionStatusEnum,
} from "@platform/openapi-types";

import {
  cleanupNotDisclosedBenefits,
  shouldIncludeBenefitInApi,
  transformPreExistingConditionFromApi,
  transformPreExistingConditionToApi,
  transformTypeAmountFromApi,
  transformTypeAmountToApi,
  transformTypeDurationFromApi,
} from "../shared/builders";

import type { CancerBenefitsApi, CancerBenefitsGrid } from "./types";

/**
 * Helper: Transform hospice benefit to API format
 * Hospice has special handling for both amount and duration fields
 */
function transformHospiceToApi(
  benefits: Partial<CancerBenefitsGrid>,
): Partial<Pick<CancerBenefitsApi, "hospice">> {
  const hospiceType = benefits.hospiceType;
  if (shouldIncludeBenefitInApi(hospiceType)) {
    const hospice: NonNullable<CancerBenefitsApi["hospice"]> = {
      type: hospiceType!,
    };

    // Only include amount and duration if type indicates benefit is included
    if (hospiceType !== RfpCancerHospiceBenefitType.notIncluded) {
      if (benefits.hospiceAmount != null) {
        hospice.amount = benefits.hospiceAmount;
      }
      if (
        benefits.hospiceDuration != null &&
        hospiceType !== RfpCancerHospiceBenefitType.dollarAmount
      ) {
        hospice.duration = benefits.hospiceDuration;
      }
    }

    return { hospice };
  }

  return {};
}

/**
 * Helper: Transform simple enum fields (portability, waiver) to API
 */
function transformSimpleEnumsToApi(
  benefits: Partial<CancerBenefitsGrid>,
): Partial<Pick<CancerBenefitsApi, "portability" | "waiverOfPremium">> {
  const result: Partial<
    Pick<CancerBenefitsApi, "portability" | "waiverOfPremium">
  > = {};

  if (shouldIncludeBenefitInApi(benefits.portability)) {
    result.portability =
      benefits.portability ?? RfpInclusionStatusEnum.notIncluded;
  }
  if (shouldIncludeBenefitInApi(benefits.waiverOfPremium)) {
    result.waiverOfPremium =
      benefits.waiverOfPremium ?? RfpInclusionStatusEnum.notIncluded;
  }

  return result;
}

/**
 * Helper: Transform type/amount benefits to API format
 * Handles the common pattern of conditionally including type/amount pairs
 */
function transformTypeAmountBenefitsToApi(
  benefits: Partial<CancerBenefitsGrid>,
): Partial<
  Pick<
    CancerBenefitsApi,
    | "diagnosisBenefit"
    | "radiationTherapyChemo"
    | "bloodPlasmaPlatelets"
    | "hospitalConfinementInitial"
    | "icuConfinementInitial"
    | "skinCancer"
    | "surgicalBenefit"
    | "wellnessBenefit"
  >
> {
  const result: Partial<
    Pick<
      CancerBenefitsApi,
      | "diagnosisBenefit"
      | "radiationTherapyChemo"
      | "bloodPlasmaPlatelets"
      | "hospitalConfinementInitial"
      | "icuConfinementInitial"
      | "skinCancer"
      | "surgicalBenefit"
      | "wellnessBenefit"
    >
  > = {};

  // Diagnosis Benefit
  if (shouldIncludeBenefitInApi(benefits.diagnosisBenefitType)) {
    result.diagnosisBenefit = transformTypeAmountToApi(
      benefits.diagnosisBenefitType,
      benefits.diagnosisBenefitAmount,
    ) as CancerBenefitsApi["diagnosisBenefit"];
  }

  // Radiation Therapy / Chemo
  if (shouldIncludeBenefitInApi(benefits.radiationTherapyChemoType)) {
    result.radiationTherapyChemo = transformTypeAmountToApi(
      benefits.radiationTherapyChemoType,
      benefits.radiationTherapyChemoAmount,
    ) as CancerBenefitsApi["radiationTherapyChemo"];
  }

  // Blood/Plasma/Platelets
  if (shouldIncludeBenefitInApi(benefits.bloodPlasmaPlateletsType)) {
    result.bloodPlasmaPlatelets = transformTypeAmountToApi(
      benefits.bloodPlasmaPlateletsType,
      benefits.bloodPlasmaPlateletsAmount,
    ) as CancerBenefitsApi["bloodPlasmaPlatelets"];
  }

  // Hospital Confinement - Initial
  if (shouldIncludeBenefitInApi(benefits.hospitalConfinementInitialType)) {
    result.hospitalConfinementInitial = transformTypeAmountToApi(
      benefits.hospitalConfinementInitialType,
      benefits.hospitalConfinementInitialAmount,
    ) as CancerBenefitsApi["hospitalConfinementInitial"];
  }

  // ICU Confinement - Initial
  if (shouldIncludeBenefitInApi(benefits.icuConfinementInitialType)) {
    result.icuConfinementInitial = transformTypeAmountToApi(
      benefits.icuConfinementInitialType,
      benefits.icuConfinementInitialAmount,
    ) as CancerBenefitsApi["icuConfinementInitial"];
  }

  // Skin Cancer (schedule benefit)
  if (shouldIncludeBenefitInApi(benefits.skinCancerType)) {
    result.skinCancer = transformTypeAmountToApi(
      benefits.skinCancerType,
      benefits.skinCancerAmount,
      ["scheduleUpToDollarAmount"],
    ) as CancerBenefitsApi["skinCancer"];
  }

  // Surgical Benefit (schedule benefit)
  if (shouldIncludeBenefitInApi(benefits.surgicalBenefitType)) {
    result.surgicalBenefit = transformTypeAmountToApi(
      benefits.surgicalBenefitType,
      benefits.surgicalBenefitAmount,
      ["scheduleUpToDollarAmount"],
    ) as CancerBenefitsApi["surgicalBenefit"];
  }

  // Wellness Benefit
  if (shouldIncludeBenefitInApi(benefits.wellnessBenefitType)) {
    result.wellnessBenefit = transformTypeAmountToApi(
      benefits.wellnessBenefitType,
      benefits.wellnessBenefitAmount,
    ) as CancerBenefitsApi["wellnessBenefit"];
  }

  return result;
}

/**
 * Transforms Cancer benefits from frontend grid format to API format
 *
 * Converts flat grid fields into nested API structures for:
 * - Diagnosis benefit (type, amount)
 * - Initial diagnosis waiting period (type, duration)
 * - Radiation therapy / chemo (type, amount)
 * - Blood/plasma/platelets (type, amount)
 * - Hospice (type, amount, duration)
 * - Hospital confinement initial (type, amount)
 * - ICU confinement initial (type, amount)
 * - Skin cancer (type, amount)
 * - Surgical benefit (type, amount)
 * - Wellness benefit (type, amount)
 *
 * @param benefits - The benefits object from the frontend grid (flat fields)
 * @returns Transformed benefits object matching the API structure (nested objects)
 */
export const transformCancerBenefitsToApi = (
  benefits: Partial<CancerBenefitsGrid>,
): CancerBenefitsApi => {
  const initialDiagnosisWaitingPeriod = shouldIncludeBenefitInApi(
    benefits.initialDiagnosisWaitingPeriodType,
  )
    ? {
        initialDiagnosisWaitingPeriod: {
          type:
            benefits.initialDiagnosisWaitingPeriodType ??
            BaseCancerBenefitInitialDiagnosisWaitingPeriodType.days,
          duration: benefits.initialDiagnosisWaitingPeriodDuration ?? 1,
        },
      }
    : undefined;

  const annualOpenEnrollment = shouldIncludeBenefitInApi(
    benefits.annualOpenEnrollment,
  )
    ? { annualOpenEnrollment: benefits.annualOpenEnrollment }
    : undefined;

  const preExistingCondition = shouldIncludeBenefitInApi(
    benefits.preExistingConditionType,
  )
    ? {
        preExistingCondition: transformPreExistingConditionToApi(
          benefits.preExistingConditionType,
          benefits.preExistingConditionMonthsDetail,
          PreExistingConditionTypeEnum.none,
          "01/01",
        ),
      }
    : undefined;

  const transformed = cleanupNotDisclosedBenefits({
    objectType: "cancer",
    ...transformTypeAmountBenefitsToApi(benefits),
    ...initialDiagnosisWaitingPeriod,
    ...transformHospiceToApi(benefits),
    ...transformSimpleEnumsToApi(benefits),
    ...annualOpenEnrollment,
    ...preExistingCondition,
  }) as unknown as CancerBenefitsApi;

  // Extended (non-v5) fields — CD-1665/CD-2075. Sent as a full object (a
  // full-replace PATCH, like the rest of `benefits`) whenever at least one
  // extended field is set, so an edit to any other field on the plan doesn't
  // drop the extended values already on it. Omitted entirely when every
  // extended field is unset, so an edit that never touches these fields
  // doesn't create an empty extended row on the backend.
  const extended = {
    eligibilityHours: benefits.eligibilityHours,
  };
  if (
    Object.values(extended).some((value) => value !== undefined && value !== "")
  ) {
    transformed.extended = extended;
  }

  return transformed;
};

/**
 * Helper: Transform hospice benefit from API to grid format
 * Hospice has special handling for both amount and duration fields
 */
function transformHospiceFromApi(
  benefits: Partial<CancerBenefitsApi>,
): Pick<
  CancerBenefitsGrid,
  "hospiceType" | "hospiceAmount" | "hospiceDuration"
> {
  if (benefits.hospice && typeof benefits.hospice === "object") {
    return {
      hospiceType: benefits.hospice.type,
      hospiceAmount: benefits.hospice.amount,
      hospiceDuration: benefits.hospice.duration,
    };
  }

  return {
    hospiceType: undefined,
    hospiceAmount: undefined,
    hospiceDuration: undefined,
  };
}

/**
 * Helper: Transform simple enum fields from API to grid
 */
function transformSimpleEnumsFromApi(
  benefits: Partial<CancerBenefitsApi>,
): Pick<CancerBenefitsGrid, "portability" | "waiverOfPremium"> {
  return {
    portability: benefits.portability,
    waiverOfPremium: benefits.waiverOfPremium,
  };
}

/**
 * Helper: Transform type/amount benefits from API to grid format
 */
function transformTypeAmountBenefitsFromApi(
  benefits: Partial<CancerBenefitsApi>,
): Partial<CancerBenefitsGrid> {
  return {
    // Diagnosis Benefit
    ...transformTypeAmountFromApi(
      benefits.diagnosisBenefit,
      "diagnosisBenefit",
    ),

    // Radiation Therapy / Chemo
    ...transformTypeAmountFromApi(
      benefits.radiationTherapyChemo,
      "radiationTherapyChemo",
    ),

    // Blood/Plasma/Platelets
    ...transformTypeAmountFromApi(
      benefits.bloodPlasmaPlatelets,
      "bloodPlasmaPlatelets",
    ),

    // Hospital Confinement - Initial
    ...transformTypeAmountFromApi(
      benefits.hospitalConfinementInitial,
      "hospitalConfinementInitial",
    ),

    // ICU Confinement - Initial
    ...transformTypeAmountFromApi(
      benefits.icuConfinementInitial,
      "icuConfinementInitial",
    ),

    // Skin Cancer
    ...transformTypeAmountFromApi(benefits.skinCancer, "skinCancer"),

    // Surgical Benefit
    ...transformTypeAmountFromApi(benefits.surgicalBenefit, "surgicalBenefit"),

    // Wellness Benefit
    ...transformTypeAmountFromApi(benefits.wellnessBenefit, "wellnessBenefit"),
  };
}

/**
 * Transforms Cancer benefits from API format to frontend grid format
 *
 * Converts nested API structures into flat grid fields for:
 * - Diagnosis benefit (type, amount)
 * - Initial diagnosis waiting period (type, duration)
 * - Radiation therapy / chemo (type, amount)
 * - Blood/plasma/platelets (type, amount)
 * - Hospice (type, amount, duration)
 * - Hospital confinement initial (type, amount)
 * - ICU confinement initial (type, amount)
 * - Skin cancer (type, amount)
 * - Surgical benefit (type, amount)
 * - Wellness benefit (type, amount)
 *
 * @param benefits - The benefits object from the API (nested objects)
 * @returns Transformed benefits object matching the grid structure (flat fields)
 */
export const transformCancerBenefitsFromApi = (
  benefits: Partial<CancerBenefitsApi> | null | undefined,
): Partial<CancerBenefitsGrid> => {
  if (!benefits) return { objectType: "cancer" };

  return {
    objectType: "cancer",
    ...transformTypeAmountBenefitsFromApi(benefits),
    ...transformTypeDurationFromApi(
      benefits.initialDiagnosisWaitingPeriod,
      "initialDiagnosisWaitingPeriod",
    ),
    ...transformHospiceFromApi(benefits),
    ...transformSimpleEnumsFromApi(benefits),
    annualOpenEnrollment: benefits.annualOpenEnrollment,
    ...transformPreExistingConditionFromApi(benefits.preExistingCondition),
    // Extended (non-v5) fields — CD-1665/CD-2075
    eligibilityHours: benefits.extended?.eligibilityHours ?? undefined,
  };
};

/** Grid keys that persist under `benefits.extended` — see STD_EXTENDED_GRID_FIELD_KEYS. */
export const CANCER_EXTENDED_GRID_FIELD_KEYS: ReadonlySet<string> = new Set([
  "eligibilityHours",
]);
